import { isAxiosError } from 'axios';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { minutesToHours } from 'date-fns';
import { rawTimeZones } from '@vvo/tzdb';

import { instance } from '@/interfaces';
import { userInfoAtom } from '@/interfaces/member';
import { selectedDeviceSafeAtom } from '@/components/common/MapView';
import { deviceDataPeriodAtom } from '@/components/devices/DeviceDetail';
import { timeZoneAtom } from '@/interfaces/utils';

import type { Getter } from 'jotai';
import type { AtomWithQueryResult } from 'jotai-tanstack-query';
import type { APIResponse } from '@/@types/types';
import type { UserInfo } from '@/@types/user';
import type {
  EndorsementData,
  EndorsementPostData,
  GroupData,
  ReportListData,
  ReportRequestData,
  ReportRequestResponse,
} from '@/@types/site';

export const groupsAtom = atomWithQuery((get) => ({
  queryKey: ['GROUPS_DATA', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    // TODO: Get current site id from API
    const data = await getGroupsData(userInfo.site[0].id);

    return data;
  },
}));
async function getGroupsData(siteId: string) {
  try {
    const { data } = await instance.get<APIResponse<GroupData[]>>(`/site/${siteId}/groups`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[getGroupData] API error code:', err.response.data.errorCode);
    } else {
      console.error('[getGroupData] Unknown error', err);
    }
    return [];
  }
}

export const mutateGroupAtom = atomWithMutation((get) => ({
  mutationFn: async (groupName: string) => {
    const { data: userInfo } = get(userInfoAtom);
    // TODO: Get current site id from API
    const isSuccess = await postGroupData(groupName, userInfo.site[0].id);
    return isSuccess;
  },
}));
async function postGroupData(groupName: string, siteId: string) {
  try {
    const { data } = await instance.post<APIResponse>(`/site/${siteId}/group`, { groupName });
    if (data.errorCode !== 'OK') {
      console.error('[postGroupData] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[postGroupData] API error code:', err.response.data.errorCode);
    } else {
      console.error('[postGroupData] Unknown error', err);
    }
    return false;
  }
}

export const reportsAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['REPORTS', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    // TODO: Get current site id from API
    const data = await getReports(userInfo.site[0].id);

    return data;
  },
}));
async function getReports(siteId: string) {
  try {
    const { data } = await instance.get<APIResponse<ReportListData[]>>(`/site/${siteId}/reports`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getReports] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getReports] Axios error message:', err.message);
      }
    } else {
      console.error('[getReports] Unknown error', err);
    }
    return [];
  }
}

export const fdaReportsAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['REPORTS', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    // TODO: Get current site id from API
    const data = await getFdaReports(userInfo.site[0].id);

    return data;
  },
}));
async function getFdaReports(siteId: string) {
  try {
    const { data } = await instance.get<APIResponse<ReportListData[]>>(`/site/${siteId}/reports`, {
      params: { reportType: 'FDA' },
    });
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getFdaReports] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getFdaReports] Axios error message:', err.message);
      }
    } else {
      console.error('[getFdaReports] Unknown error', err);
    }
    return [];
  }
}

export const postReportRequestAtom = atomWithMutation((get) => ({
  mutationFn: async ({ unit, format }: { unit: ReportRequestData['unit']; format: string }) => {
    const { data: userInfo } = get(userInfoAtom);
    const { id: sensorId } = get(selectedDeviceSafeAtom);
    const { from, to } = get(deviceDataPeriodAtom);
    const timezone = get(timeZoneAtom);
    const offset = getOffset(timezone);

    let jobId: string;
    const body: ReportRequestData = {
      sensorId,
      creatorId: userInfo.id,
      unit,
      from: from.toISOString(),
      to: to.toISOString(),
      offset,
    };

    // TODO: Get current site id from API
    if (format === 'pdf') {
      jobId = await postPDFReportRequest(userInfo.site[0].id, body);
    } else if (format === 'csv') {
      jobId = await postCSVReportRequest(userInfo.site[0].id, body);
    } else if (format === 'fda') {
      jobId = await postFDAReportRequest(userInfo.site[0].id, body);
    }

    return jobId;
  },
}));
async function postPDFReportRequest(siteId: string, body: ReportRequestData) {
  try {
    // FIXME: Remove timeout setting after API fix
    const { data } = await instance.post<APIResponse<ReportRequestResponse>>(`/site/${siteId}/analysis-report`, body, {
      timeout: 60 * 1000,
    });
    if (data.errorCode !== 'OK') {
      console.error('[postPDFReportRequest] API error code:', data.errorCode);
      return null;
    }
    return data.data.id;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postPDFReportRequest] API error code:', err.response.data.errorCode);
      } else {
        console.error('[postPDFReportRequest] Axios error message:', err.message);
      }
    } else {
      console.error('[postPDFReportRequest] Unknown error', err);
    }
    return null;
  }
}
async function postCSVReportRequest(siteId: string, body: ReportRequestData) {
  try {
    const { data } = await instance.post<APIResponse<ReportRequestResponse>>(`/site/${siteId}/data-sheet`, body);
    if (data.errorCode !== 'OK') {
      console.error('[postCSVReportRequest] API error code:', data.errorCode);
      return null;
    }
    return data.data.id;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postCSVReportRequest] API error code:', err.response.data.errorCode);
      } else {
        console.error('[postCSVReportRequest] Axios error message:', err.message);
      }
    } else {
      console.error('[postCSVReportRequest] Unknown error', err);
    }
    return null;
  }
}
async function postFDAReportRequest(siteId: string, body: ReportRequestData) {
  try {
    // FIXME: Remove timeout setting after API fix
    const { data } = await instance.post<APIResponse<ReportRequestResponse>>(`/site/${siteId}/fda-report`, body, {
      timeout: 60 * 1000,
    });
    if (data.errorCode !== 'OK') {
      console.error('[postFDAReportRequest] API error code:', data.errorCode);
      return null;
    }
    return data.data.id;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postFDAReportRequest] API error code:', err.response.data.errorCode);
      } else {
        console.error('[postFDAReportRequest] Axios error message:', err.message);
      }
    } else {
      console.error('[postFDAReportRequest] Unknown error', err);
    }
    return null;
  }
}
function getOffset(timezone: string) {
  let offsetInMinutes = rawTimeZones.find((tz) => tz.name === timezone).rawOffsetInMinutes;
  const minus = offsetInMinutes < 0 ? '-' : '+';
  offsetInMinutes = Math.abs(offsetInMinutes);

  const hours = minutesToHours(offsetInMinutes).toString().padStart(2, '0');
  const minutes = (offsetInMinutes % 60).toString().padStart(2, '0');

  return minus + hours + minutes;
}

export const endorsementsAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['ENDORSEMENTS', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    // TODO: Get current site id from API
    const data = await getEndorsements(userInfo.site[0].id);

    return data;
  },
}));
async function getEndorsements(siteId: string) {
  try {
    const { data } = await instance.get<APIResponse<EndorsementData[]>>(`/site/${siteId}/endorsement`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getEndorsements] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getEndorsements] Axios error message:', err.message);
      }
    } else {
      console.error('[getEndorsements] Unknown error', err);
    }
    return [];
  }
}

export const postEndorsementAtom = atomWithMutation((get: Getter) => ({
  mutationFn: async (body: EndorsementPostData) => {
    const { data: userInfo } = get(userInfoAtom);
    // TODO: Get current site id from API
    const isSuccess = await postEndorsement(userInfo.site[0].id, body);
    return isSuccess;
  },
}));
async function postEndorsement(siteId: string, body: EndorsementPostData) {
  try {
    const { data } = await instance.post<APIResponse>(`/site/${siteId}/endorsement`, body);
    if (data.errorCode !== 'OK') {
      console.error('[postEndorsement] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postEndorsement] API error code:', err.response.data.errorCode);
      } else {
        console.error('[postEndorsement] Axios error message:', err.message);
      }
    } else {
      console.error('[postEndorsement] Unknown error', err);
    }
    return false;
  }
}
